import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Loader2,
  Save,
  Send,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { STEPS } from "@/components/assessment/sections";
import VoiceInterview from "@/components/assessment/VoiceInterview";

const SECTION_FIELDS = [
  "current_situation",
  "work_history",
  "skills",
  "education",
  "hobbies",
  "personality",
  "physical_constraints",
  "salary",
  "financial_position",
  "location_travel_relocation",
  "retraining_capacity",
  "government_support",
  "career_goals",
];

function pickSections(data, keys) {
  const out = {};
  for (const k of keys) out[k] = data[k];
  return out;
}

export default function Assessment() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [errors, setErrors] = useState({});
  const [savingDraft, setSavingDraft] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [topError, setTopError] = useState("");
  const [submittedOk, setSubmittedOk] = useState(false);

  const dirtyRef = useRef(false);

  // ---- load initial assessment ----
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data: doc } = await api.get("/assessment/me");
        if (!active) return;
        setData(doc);
      } catch (err) {
        if (!active) return;
        setTopError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const step = STEPS[stepIdx];
  const totalSteps = STEPS.length;
  const progressPct = Math.round(((stepIdx + 1) / totalSteps) * 100);

  const onSectionChange = (patch) => {
    dirtyRef.current = true;
    setData((d) => ({ ...d, ...patch }));
  };

  // Merge voice-extracted fields into the assessment without overwriting
  // anything the user already typed. Arrays are concatenated; objects are
  // shallow-merged so nested keys like current_situation.role_title are
  // preserved when the user only spoke about role_title (not industry).
  const onApplyVoiceFields = (fields) => {
    if (!fields) return;
    dirtyRef.current = true;
    setData((prev) => {
      const next = { ...prev };
      for (const [k, v] of Object.entries(fields)) {
        if (Array.isArray(v)) {
          const existing = Array.isArray(next[k]) ? next[k] : [];
          next[k] = [...existing, ...v];
        } else if (v && typeof v === "object") {
          next[k] = { ...(next[k] || {}), ...v };
        } else if (v != null && v !== "") {
          next[k] = v;
        }
      }
      return next;
    });
  };

  const persist = async (patchKeys) => {
    if (!data) return null;
    const body = pickSections(data, patchKeys);
    const { data: doc } = await api.patch("/assessment/me", body);
    dirtyRef.current = false;
    setSavedAt(new Date());
    return doc;
  };

  const onSaveDraft = async () => {
    if (!data || savingDraft) return;
    setSavingDraft(true);
    setTopError("");
    try {
      await persist(SECTION_FIELDS);
    } catch (err) {
      setTopError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingDraft(false);
    }
  };

  const validateCurrent = () => {
    if (typeof step.validate !== "function") return {};
    return step.validate(data) || {};
  };

  const onNext = async () => {
    const e = validateCurrent();
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    try {
      await persist(step.sections);
    } catch (err) {
      setTopError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      return;
    }
    setStepIdx((i) => Math.min(i + 1, totalSteps - 1));
    setErrors({});
    setTopError("");
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onBack = async () => {
    if (stepIdx === 0) return;
    try {
      if (dirtyRef.current) await persist(step.sections);
    } catch (err) {
      // Don't block back nav on autosave failure, but log for diagnostics.
      // eslint-disable-next-line no-console
      console.warn("[assessment] autosave on Back failed:", err?.message || err);
    }
    setStepIdx((i) => Math.max(i - 1, 0));
    setErrors({});
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onSubmit = async () => {
    if (!data || submitting) return;
    const e = validateCurrent();
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setSubmitting(true);
    setTopError("");
    try {
      await persist(SECTION_FIELDS);
      await api.post("/assessment/me/submit");
      setSubmittedOk(true);
      setTimeout(() => navigate("/assessment/review", { replace: true }), 1200);
    } catch (err) {
      setTopError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const savedHint = savedAt ? "Saved" : null;

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="assessment-page">
        <Navbar />
        <main className="pt-24 lg:pt-28 pb-20">
          <div className="max-w-4xl mx-auto px-6 lg:px-12 flex items-center gap-3 text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading your assessment…
          </div>
        </main>
      </div>
    );
  }

  if (submittedOk) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="assessment-page">
        <Navbar />
        <main className="pt-24 lg:pt-28 pb-20">
          <div
            className="max-w-2xl mx-auto px-6 lg:px-12 text-center"
            data-testid="assessment-submitted"
          >
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
              <CheckCircle2 className="h-7 w-7" />
            </span>
            <h1 className="mt-6 text-3xl font-medium tracking-tight text-slate-900">
              Thank you. Your assessment is in.
            </h1>
            <p className="mt-3 text-base text-slate-600">
              {"We'll start preparing your personalised report. Taking you back to your dashboard…"}
            </p>
          </div>
        </main>
      </div>
    );
  }

  const StepComp = step.Component;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="assessment-page">
      <Navbar />
      <main className="pt-24 lg:pt-28 pb-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          {/* Heading */}
          <div className="flex flex-col gap-2">
            <Link
              to="/dashboard"
              className="text-sm text-slate-500 hover:text-slate-700 inline-flex items-center gap-1.5"
              data-testid="assessment-back-to-dashboard"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to dashboard
            </Link>
            <h1
              className="text-3xl lg:text-4xl font-medium tracking-tight text-slate-900"
              data-testid="assessment-heading"
            >
              Career Reset Assessment
            </h1>
            <p className="text-base text-slate-600">
              {`Hi ${user?.name || "there"} — this takes about 15 minutes. You can save and come back anytime.`}
            </p>
          </div>

          {/* Progress */}
          <div className="mt-10" data-testid="assessment-progress">
            <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
              <span data-testid="assessment-step-counter">
                {`Step ${stepIdx + 1} of ${totalSteps}`}
              </span>
              <span data-testid="assessment-progress-percent">{`${progressPct}%`}</span>
            </div>
            <div className="h-2 w-full rounded-full bg-slate-200 overflow-hidden">
              <div
                className="h-full bg-slate-900 transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <ol className="mt-4 flex flex-wrap gap-x-3 gap-y-2 text-xs">
              {STEPS.map((s, i) => {
                const state =
                  i < stepIdx ? "done" : i === stepIdx ? "current" : "upcoming";
                return (
                  <li
                    key={s.key}
                    className={
                      "inline-flex items-center gap-1.5 " +
                      (state === "current"
                        ? "text-slate-900 font-medium"
                        : state === "done"
                        ? "text-emerald-700"
                        : "text-slate-400")
                    }
                    data-testid={`progress-step-${i}-${state}`}
                  >
                    <span
                      className={
                        "inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] " +
                        (state === "current"
                          ? "bg-slate-900 text-white"
                          : state === "done"
                          ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
                          : "bg-slate-100 text-slate-500")
                      }
                    >
                      {i + 1}
                    </span>
                    <span className="hidden sm:inline">{s.title}</span>
                  </li>
                );
              })}
            </ol>
          </div>

          {/* Card */}
          <section className="mt-10 rounded-3xl border border-slate-200 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-7 lg:p-10">
            <header className="mb-8">
              <p className="text-sm uppercase tracking-[0.18em] text-sky-700 font-medium">
                {`${stepIdx + 1}. ${step.title}`}
              </p>
              <h2
                className="mt-1.5 text-2xl lg:text-3xl font-medium tracking-tight text-slate-900"
                data-testid="assessment-step-title"
              >
                {step.title}
              </h2>
              {step.subtitle ? (
                <p className="mt-2 text-base text-slate-600">{step.subtitle}</p>
              ) : null}
            </header>

            {topError ? (
              <div
                className="mb-6 rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-sm text-red-700"
                data-testid="assessment-top-error"
              >
                {topError}
              </div>
            ) : null}

            <StepComp data={data} onChange={onSectionChange} errors={errors} />
          </section>

          {/* Controls */}
          <div
            className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
            data-testid="assessment-controls"
          >
            <div className="flex items-center gap-3 text-xs text-slate-500">
              <button
                type="button"
                onClick={onSaveDraft}
                disabled={savingDraft}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                data-testid="assessment-save-draft-button"
              >
                {savingDraft ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {savingDraft ? "Saving…" : "Save draft"}
              </button>
              {savedHint ? (
                <span data-testid="assessment-saved-hint">{savedHint}</span>
              ) : null}
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onBack}
                disabled={stepIdx === 0}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="assessment-back-button"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>

              {stepIdx < totalSteps - 1 ? (
                <button
                  type="button"
                  onClick={onNext}
                  className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-medium text-white shadow-md hover:bg-slate-800 hover:-translate-y-0.5 transition-all"
                  data-testid="assessment-next-button"
                >
                  Next
                  <ArrowRight className="h-4 w-4" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onSubmit}
                  disabled={submitting}
                  className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-medium text-white shadow-md hover:bg-slate-800 hover:-translate-y-0.5 transition-all disabled:opacity-60"
                  data-testid="assessment-submit-button"
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                  {submitting ? "Submitting…" : "Submit assessment"}
                </button>
              )}
            </div>
          </div>
        </div>
      </main>
      <VoiceInterview
        context={`Step ${stepIdx + 1} of ${totalSteps}: ${step?.label || ""}`}
        onApplyFields={onApplyVoiceFields}
      />
    </div>
  );
}
